import React, { useMemo, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import DateTimePicker, {
  type DateTimePickerEvent,
} from '@react-native-community/datetimepicker';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { minutesToClock, parseClockToMinutes } from '../utils/checkoutBreak';
import { formatTime } from '../utils/dateTime';

type Props = {
  open: boolean;
  value: string;
  onClose: () => void;
  onConfirm: (value: string) => void;
};

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 60 }, (_, index) => index);
const PERIODS = ['AM', 'PM'] as const;

function dateFromClock(value: string): Date {
  const minutes = parseClockToMinutes(value);
  const date = new Date();
  if (minutes === null) return date;
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return date;
}

function clockFromDate(date: Date): string {
  return minutesToClock(date.getHours() * 60 + date.getMinutes()) ?? '00:00';
}

function hour12FromDate(date: Date): number {
  const hour = date.getHours() % 12;
  return hour === 0 ? 12 : hour;
}

function applyClockParts(
  hour12: number,
  minute: number,
  period: (typeof PERIODS)[number],
): Date {
  let hour = hour12 % 12;
  if (period === 'PM') hour += 12;
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date;
}

function WebTimeModal({
  value,
  onClose,
  onConfirm,
}: Omit<Props, 'open'>) {
  const palette = useAppColors();
  const selected = dateFromClock(value);
  const [hour12, setHour12] = useState(() => hour12FromDate(selected));
  const [minute, setMinute] = useState(() => selected.getMinutes());
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>(() =>
    selected.getHours() >= 12 ? 'PM' : 'AM',
  );
  const preview = useMemo(
    () => formatTime(clockFromDate(applyClockParts(hour12, minute, period))),
    [hour12, minute, period],
  );

  return (
    <Modal transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.webRoot}>
        <Pressable
          style={styles.webOverlay}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Dismiss time picker"
        />
        <View
          accessibilityRole="summary"
          accessibilityLabel="Time picker"
          style={[styles.webSheet, { backgroundColor: palette.surface }]}
        >
          <Text style={[styles.webPreview, { color: palette.textPrimary }]}>
            {preview}
          </Text>
          <View style={styles.webColumns}>
            <View style={styles.webColumn}>
              <Text style={[styles.webColumnTitle, { color: palette.textMuted }]}>
                Hour
              </Text>
              <ScrollView style={styles.webScroll} nestedScrollEnabled>
                {HOURS_12.map(item => {
                  const selectedHour = item === hour12;
                  return (
                    <Pressable
                      key={`hour-${item}`}
                      accessibilityRole="button"
                      accessibilityLabel={`Hour ${item}`}
                      accessibilityState={{ selected: selectedHour }}
                      onPress={() => setHour12(item)}
                      style={[
                        styles.webOption,
                        { cursor: 'pointer' as const },
                        selectedHour
                          ? { backgroundColor: palette.primary, borderRadius: 8 }
                          : null,
                      ]}
                    >
                      <Text
                        style={[
                          styles.webOptionLabel,
                          {
                            color: selectedHour
                              ? palette.onPrimary
                              : palette.textPrimary,
                          },
                        ]}
                      >
                        {String(item)}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
            <View style={styles.webColumn}>
              <Text style={[styles.webColumnTitle, { color: palette.textMuted }]}>
                Minute
              </Text>
              <ScrollView style={styles.webScroll} nestedScrollEnabled>
                {MINUTES.map(item => {
                  const selectedMinute = item === minute;
                  return (
                    <Pressable
                      key={`minute-${item}`}
                      accessibilityRole="button"
                      accessibilityLabel={`Minute ${String(item).padStart(2, '0')}`}
                      accessibilityState={{ selected: selectedMinute }}
                      onPress={() => setMinute(item)}
                      style={[
                        styles.webOption,
                        { cursor: 'pointer' as const },
                        selectedMinute
                          ? { backgroundColor: palette.primary, borderRadius: 8 }
                          : null,
                      ]}
                    >
                      <Text
                        style={[
                          styles.webOptionLabel,
                          {
                            color: selectedMinute
                              ? palette.onPrimary
                              : palette.textPrimary,
                          },
                        ]}
                      >
                        {String(item).padStart(2, '0')}
                      </Text>
                    </Pressable>
                  );
                })}
              </ScrollView>
            </View>
            <View style={styles.webColumn}>
              <Text style={[styles.webColumnTitle, { color: palette.textMuted }]}>
                Period
              </Text>
              {PERIODS.map(item => {
                const selectedPeriod = item === period;
                return (
                  <Pressable
                    key={item}
                    accessibilityRole="button"
                    accessibilityLabel={item}
                    accessibilityState={{ selected: selectedPeriod }}
                    onPress={() => setPeriod(item)}
                    style={[
                      styles.webOption,
                      { cursor: 'pointer' as const },
                      selectedPeriod
                        ? { backgroundColor: palette.primary, borderRadius: 8 }
                        : null,
                    ]}
                  >
                    <Text
                      style={[
                        styles.webOptionLabel,
                        {
                          color: selectedPeriod
                            ? palette.onPrimary
                            : palette.textPrimary,
                        },
                      ]}
                    >
                      {item}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
          <View style={styles.webToolbar}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              onPress={onClose}
              style={[styles.webAction, { cursor: 'pointer' as const }]}
            >
              <Text style={[styles.iosAction, { color: palette.textSecondary }]}>
                Cancel
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Done"
              onPress={() =>
                onConfirm(clockFromDate(applyClockParts(hour12, minute, period)))
              }
              style={[styles.webAction, { cursor: 'pointer' as const }]}
            >
              <Text style={[styles.iosAction, { color: palette.primary }]}>
                Done
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

export function TimePicker({ open, value, onClose, onConfirm }: Props) {
  const palette = useAppColors();
  const [iosDraft, setIosDraft] = useState(() => dateFromClock(value));
  const isWeb = (Platform.OS as string) === 'web';

  React.useEffect(() => {
    if (open) setIosDraft(dateFromClock(value));
  }, [open, value]);

  if (!open) return null;

  const onAndroidChange = (event: DateTimePickerEvent, date?: Date) => {
    if (event.type === 'dismissed') {
      onClose();
      return;
    }
    if (event.type === 'set' && date) {
      onConfirm(clockFromDate(date));
      return;
    }
    onClose();
  };

  if (isWeb) {
    return (
      <WebTimeModal value={value} onClose={onClose} onConfirm={onConfirm} />
    );
  }

  if (Platform.OS === 'android') {
    return (
      <DateTimePicker
        value={dateFromClock(value)}
        mode="time"
        display="spinner"
        onChange={onAndroidChange}
      />
    );
  }

  return (
    <Modal transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.iosRoot}>
        <Pressable
          style={styles.iosOverlay}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Dismiss time picker"
        />
        <View style={[styles.iosSheet, { backgroundColor: palette.surface }]}>
          <View style={styles.iosToolbar}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              onPress={onClose}
            >
              <Text style={[styles.iosAction, { color: palette.textSecondary }]}>
                Cancel
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Done"
              onPress={() => onConfirm(clockFromDate(iosDraft))}
            >
              <Text style={[styles.iosAction, { color: palette.primary }]}>
                Done
              </Text>
            </Pressable>
          </View>
          <DateTimePicker
            value={iosDraft}
            mode="time"
            display="spinner"
            onChange={(_event, date) => {
              if (date) setIosDraft(date);
            }}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  iosRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  iosOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  iosSheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: spacing.xl,
  },
  iosToolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  iosAction: {
    ...typography.body,
    fontWeight: '600',
  },
  webRoot: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    zIndex: 1000,
  },
  webOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  webSheet: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 16,
    padding: spacing.lg,
    zIndex: 1,
  },
  webPreview: {
    ...typography.sectionTitle,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  webColumns: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  webColumn: {
    flex: 1,
  },
  webColumnTitle: {
    ...typography.caption,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  webScroll: {
    maxHeight: 220,
  },
  webOption: {
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  webOptionLabel: {
    ...typography.body,
    fontWeight: '600',
  },
  webToolbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.md,
  },
  webAction: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
});
